/**
 * GAME 7 — FILAMENTS (numberlink / flow). Découpe la grille en chemins reliant des paires.
 * Durci (phase difficulté-score) : rochers infranchissables sur les défis sans couverture,
 * distance minimale entre les sources d'une paire, minL/quotas de paires par niveau,
 * détours imposés (Sur mesure), fleuve minimal (Grand fleuve), `par` = Σ(|solution| − 1).
 */
import type { GameEngine, PuzzleBase, Rng } from '@ordre-cache/shared';
import { ri, shuffle } from '@ordre-cache/shared';
import { D4, deep, mk } from './util';

export interface FilamentPair {
  a: [number, number];
  b: [number, number];
  col: number;
  solPath: [number, number][];
  goal: number | null;
  path: [number, number][];
}

export interface FilamentsPuzzle extends PuzzleBase {
  g: 'filaments';
  pairs: FilamentPair[];
  /** Cases infranchissables (défis sans couverture) — jamais sur une solution ni une source. */
  rocks: [number, number][];
  req: { cover: boolean; lens: boolean; riverIdx: number | null };
}

function flCarve(
  N: number,
  minL: number,
  maxPairs: number,
  rng: Rng,
  boost = 0,
): [number, number][][] | null {
  for (let tries = 0; tries < 120; tries++) {
    const own = mk(N, -1);
    const paths: [number, number][][] = [];
    const order: number[] = [];
    for (let i = 0; i < N * N; i++) order.push(i);
    shuffle(rng, order);
    for (const cell of order) {
      const r0 = Math.floor(cell / N),
        c0 = cell % N;
      if (own[r0][c0] !== -1) continue;
      const idx = paths.length,
        path: [number, number][] = [[r0, c0]];
      own[r0][c0] = idx;
      const maxL = minL + 2 + ri(rng, minL + 1) + (boost ? ri(rng, boost + 1) : 0);
      while (path.length < maxL) {
        const h = path[path.length - 1],
          opts: [number, number][] = [];
        for (const [dr, dc] of D4) {
          const rr = h[0] + dr,
            cc = h[1] + dc;
          if (rr >= 0 && rr < N && cc >= 0 && cc < N && own[rr][cc] === -1) opts.push([rr, cc]);
        }
        if (!opts.length) break;
        const nx = opts[ri(rng, opts.length)];
        own[nx[0]][nx[1]] = idx;
        path.push(nx);
      }
      paths.push(path);
    }
    // absorber les cases libres par les extrémités des chemins voisins
    let moved = true;
    while (moved) {
      moved = false;
      for (let r = 0; r < N; r++)
        for (let c = 0; c < N; c++) {
          if (own[r][c] !== -1) continue;
          for (const [dr, dc] of D4) {
            const rr = r + dr,
              cc = c + dc;
            if (rr < 0 || rr >= N || cc < 0 || cc >= N || own[rr][cc] === -1) continue;
            const pi = own[rr][cc],
              pa = paths[pi];
            const atStart = pa[0][0] === rr && pa[0][1] === cc;
            const atEnd = pa[pa.length - 1][0] === rr && pa[pa.length - 1][1] === cc;
            if (atStart || atEnd) {
              if (atStart) pa.unshift([r, c]);
              else pa.push([r, c]);
              own[r][c] = pi;
              moved = true;
              break;
            }
          }
        }
    }
    let free = 0;
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (own[r][c] === -1) free++;
    if (free) continue;
    // fusionner les chemins trop courts bout à bout
    const endAdj = (a: [number, number], b: [number, number]): boolean =>
      Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) === 1;
    let merged = true;
    while (merged) {
      merged = false;
      for (let i = 0; i < paths.length && !merged; i++) {
        if (paths[i].length >= Math.max(2, minL)) continue;
        const pa = paths[i];
        for (let j = 0; j < paths.length && !merged; j++) {
          if (j === i) continue;
          const pb = paths[j];
          let np: [number, number][] | null = null;
          if (endAdj(pb[pb.length - 1], pa[0])) np = pb.concat(pa);
          else if (endAdj(pb[pb.length - 1], pa[pa.length - 1]))
            np = pb.concat(pa.slice().reverse());
          else if (endAdj(pb[0], pa[pa.length - 1])) np = pa.concat(pb);
          else if (endAdj(pb[0], pa[0])) np = pa.slice().reverse().concat(pb);
          if (np) {
            paths[j] = np;
            paths.splice(i, 1);
            merged = true;
          }
        }
      }
    }
    if (paths.some((p) => p.length < 2)) continue;
    if (paths.length < 2 || paths.length > maxPairs) continue;
    return paths;
  }
  return null;
}

/** BFS plus court chemin de a à b en évitant `blocked` (clés r*N+c). */
function flBfs(
  N: number,
  a: [number, number],
  b: [number, number],
  blocked: Set<number>,
): [number, number][] | null {
  const prev = new Map<number, number>();
  prev.set(a[0] * N + a[1], -1);
  const q: [number, number][] = [a];
  for (let qi = 0; qi < q.length; qi++) {
    const [r, c] = q[qi];
    if (r === b[0] && c === b[1]) {
      const path: [number, number][] = [];
      let cur = r * N + c;
      while (cur !== -1) {
        path.push([Math.floor(cur / N), cur % N]);
        cur = prev.get(cur) as number;
      }
      return path.reverse();
    }
    for (const [dr, dc] of D4) {
      const rr = r + dr,
        cc = c + dc;
      if (rr < 0 || rr >= N || cc < 0 || cc >= N) continue;
      const kk = rr * N + cc;
      if (prev.has(kk) || blocked.has(kk)) continue;
      prev.set(kk, r * N + c);
      q.push([rr, cc]);
    }
  }
  return null;
}

/**
 * Simule le bot « plus court chemin » myope (une passe, paire par paire).
 * true = le bot gagne. Sert à préférer des tirages qui exigent de la réflexion.
 */
function flBotWins(N: number, chosen: [number, number][][], rocks: [number, number][]): boolean {
  const drawn: [number, number][][] = [];
  for (let i = 0; i < chosen.length; i++) {
    const blocked = new Set<number>();
    for (const rk of rocks) blocked.add(rk[0] * N + rk[1]);
    for (let j = 0; j < chosen.length; j++) {
      if (j === i) continue;
      const q = chosen[j];
      blocked.add(q[0][0] * N + q[0][1]);
      blocked.add(q[q.length - 1][0] * N + q[q.length - 1][1]);
    }
    for (const dp of drawn) for (const c of dp) blocked.add(c[0] * N + c[1]);
    const path = flBfs(N, chosen[i][0], chosen[i][chosen[i].length - 1], blocked);
    if (!path) return false;
    drawn.push(path);
  }
  return true;
}

const FL_COLS = [
  '#ef476f',
  '#ffd166',
  '#06d6a0',
  '#4cc9f0',
  '#b388ff',
  '#ff9e6d',
  '#f15bb5',
  '#80ed99',
  '#e0e0e0',
  '#ffb703',
  '#90e0ef',
  '#c9184a',
];

function pairDone(pr: FilamentPair): boolean {
  if (pr.path.length < 2) return false;
  const s = pr.path[0],
    e = pr.path[pr.path.length - 1];
  const okends =
    (s[0] === pr.a[0] && s[1] === pr.a[1] && e[0] === pr.b[0] && e[1] === pr.b[1]) ||
    (s[0] === pr.b[0] && s[1] === pr.b[1] && e[0] === pr.a[0] && e[1] === pr.a[1]);
  return okends;
}

let sel: number | null = null;

export const FILAMENTS: GameEngine<FilamentsPuzzle> = {
  id: 'filaments',
  name: 'Filaments',
  icon: '🌊',
  accent: '#00b4d8',
  tag: 'Relie les sources, sans jamais croiser.',
  how: "<b>Touche une source</b> (rond coloré) pour la prendre en main, puis <b>avance case par case</b> jusqu'à sa jumelle. Les chemins ne se croisent jamais. Les cases sombres sont des <b>rochers</b> : aucun fil ne les traverse. Touche ton propre chemin pour revenir en arrière.",
  sizes: [5, 7, 9, 12],
  defis: [
    {
      n: 'Fil à fil',
      d: 'Relie chaque paire de couleurs en te faufilant entre les rochers.',
      ic: '🧵',
    },
    { n: 'Tout couvrir', d: 'Aucune case libre : les fils occupent tout le terrain.', ic: '🗺️' },
    {
      n: 'Sur mesure',
      d: 'Chaque fil doit mesurer pile la longueur annoncée — les rochers imposent le détour.',
      ic: '📏',
    },
    {
      n: 'Delta',
      d: "Une foule de paires s'entremêle entre les rochers : à chacune son chemin.",
      ic: '🔀',
    },
    {
      n: 'Grand fleuve',
      d: 'Un fil interminable doit serpenter entre les rochers de la grille.',
      ic: '🐍',
    },
    {
      n: 'Crue totale',
      d: 'Tout couvrir, longueurs exactes : la grille sort de son lit.',
      ic: '🌊',
    },
  ],
  gen(di, N, diff, rng) {
    const cover = di === 1 || di === 5;
    const lens = di === 2 || di === 5;
    // Delta garde minL court : son levier de difficulté est le NOMBRE de paires
    // (quota [5,7,9] infaisable sur 5×5 avec des fils longs). Au niveau 2 on carve
    // plus long puis on SCINDE : les coupes contrôlées donnent des fils sinueux.
    let minL = di === 3 ? (diff === 2 ? 3 : 2) : [3, 4, 5][diff];
    minL += N >= 9 ? 2 : N >= 7 ? 1 : 0;
    const minDist = di === 3 ? 2 : Math.max(3, Math.ceil(N / 2) - 1);
    const want = di === 3 ? [5, 7, 9][diff] : [3, 4, 5][diff];
    const riverMin = di === 4 ? Math.ceil([1.5, 2, 2.5][diff] * N) : 0;
    const dist = (p: [number, number][]): number =>
      Math.abs(p[0][0] - p[p.length - 1][0]) + Math.abs(p[0][1] - p[p.length - 1][1]);
    const detour = (p: [number, number][]): boolean => p.length >= dist(p) + 3;
    const detNeed = (n: number): number =>
      lens ? (diff === 2 ? n : diff === 1 ? Math.ceil((2 * n) / 3) : 0) : 0;

    // Anti-bot (défis Fil à fil / Delta) : exiger — avec une probabilité graduée par
    // niveau — un tirage que le routage myope « plus court chemin » ne résout pas.
    const antiBot = (di === 0 || di === 3) && rng() < [0.6, 0.85, 1][diff];

    // ≤ 30 découpages : on garde le meilleur tirage, arrêt dès qu'un tirage est parfait
    // (gen réussit TOUJOURS — les contraintes sont des scores, jamais des exceptions).
    let best: { chosen: [number, number][][]; rocks: [number, number][] } | null = null;
    let bestScore = -1;
    for (let t = 0; t < 30; t++) {
      // hors couverture, seul un sous-ensemble est retenu : pas de plafond de couleurs
      const paths = flCarve(N, minL, cover ? FL_COLS.length : N * N, rng, di === 4 ? riverMin : 0);
      if (!paths) continue;
      const sorted = paths.slice().sort((a, b) => b.length - a.length);
      let chosen: [number, number][][];
      let quotaOk = true;
      if (cover) chosen = sorted;
      else {
        let pool = sorted.filter((p) => p.length >= Math.max(2, minL) && dist(p) >= minDist);
        if (di === 3) {
          // Delta : scinder les fils longs (deux moitiés ≥ 3) pour atteindre le quota.
          // On préfère les coupes donnant des moitiés SINUEUSES (len > dist+1) :
          // le bot « plus court chemin » quitte alors la solution et bloque autrui.
          const wig = (p: [number, number][]): number => p.length - dist(p) - 1;
          let ix = 0;
          while (pool.length < want && ix < pool.length) {
            const p = pool[ix];
            let bestCut: [[number, number][], [number, number][]] | null = null;
            let bestW = -1;
            for (let cut = 3; p.length >= 6 && cut <= p.length - 3; cut++) {
              const pa = p.slice(0, cut),
                pb = p.slice(cut);
              if (dist(pa) < minDist || dist(pb) < minDist) continue;
              const w = Math.min(1, wig(pa)) + Math.min(1, wig(pb)) + rng() * 0.5;
              if (w > bestW) {
                bestW = w;
                bestCut = [pa, pb];
              }
            }
            if (bestCut) {
              pool.splice(ix, 1, bestCut[0], bestCut[1]);
              continue;
            }
            ix++;
          }
          // diversité géométrique + sinuosité d'abord (le quota prime sur la longueur)
          shuffle(rng, pool);
          pool.sort((a, b) => Math.min(2, wig(b)) - Math.min(2, wig(a)));
        }
        quotaOk = pool.length >= want;
        if (di === 2 && diff >= 1)
          pool = pool
            .slice()
            .sort((a, b) => (detour(b) ? 1 : 0) - (detour(a) ? 1 : 0) || b.length - a.length);
        chosen = pool.slice(0, want);
        // relâchement : jamais moins de 2 paires si le découpage en fournit
        for (const p of sorted) {
          if (chosen.length >= Math.min(2, want)) break;
          if (!chosen.includes(p)) chosen.push(p);
        }
        chosen.sort((a, b) => b.length - a.length);
      }
      // rochers du tirage : une fraction des cases hors solutions choisies
      const rocks: [number, number][] = [];
      if (!cover) {
        const covered = mk(N, false);
        for (const p of chosen) for (const c of p) covered[c[0]][c[1]] = true;
        const free: [number, number][] = [];
        for (let r = 0; r < N; r++)
          for (let c = 0; c < N; c++) if (!covered[r][c]) free.push([r, c]);
        shuffle(rng, free);
        rocks.push(...free.slice(0, Math.round(free.length * [0.35, 0.7, 1.0][diff])));
      }
      const n = chosen.length;
      const distOk = chosen.filter((p) => dist(p) >= minDist).length;
      const need = detNeed(n);
      const detOk = Math.min(chosen.filter(detour).length, need);
      const riverLen = di === 4 && n ? chosen[0].length : 0;
      // Battre le bot ne doit pas sacrifier le quota-plancher de paires (Delta).
      const nFloor = di === 3 ? [5, 6, 7][diff] : 2;
      // le bot myope est sensible à l'ordre et au sens des paires : plusieurs essais
      let botBeaten = false;
      if (antiBot && n >= nFloor) {
        botBeaten = !flBotWins(N, chosen, rocks);
        for (let k = 0; k < 19 && !botBeaten; k++) {
          const cand = shuffle(
            rng,
            chosen.map((p) => (rng() < 0.5 ? p.slice().reverse() : p)),
          );
          if (!flBotWins(N, cand, rocks)) {
            chosen = cand;
            botBeaten = true;
          }
        }
      }
      const score = cover
        ? Math.round((distOk / Math.max(1, n)) * 1e4) + Math.round((need ? detOk / need : 1) * 100)
        : // battre le bot vaut ~1,5 paire : on peut en sacrifier UNE, pas deux
          n * 1e6 +
          (botBeaten ? 1_500_000 : 0) +
          distOk * 1e4 +
          detOk * 100 +
          Math.min(riverLen, riverMin);
      if (score > bestScore) {
        bestScore = score;
        best = { chosen, rocks };
      }
      if (quotaOk && distOk === n && detOk >= need && riverLen >= riverMin && (!antiBot || botBeaten))
        break;
    }
    if (!best || !best.chosen.length) {
      return {
        g: 'filaments',
        pid: ri(rng, 1e9),
        N,
        di,
        pairs: [],
        rocks: [],
        req: { cover: false, lens: false, riverIdx: null },
      };
    }
    const pairs: FilamentPair[] = best.chosen.map((p, k) => ({
      a: p[0],
      b: p[p.length - 1],
      col: k % FL_COLS.length,
      solPath: p,
      goal: lens ? p.length : null,
      path: [],
    }));
    const req = { cover, lens, riverIdx: di === 4 ? 0 : null };
    if (di === 4) pairs[0].goal = pairs[0].solPath.length;
    const par = best.chosen.reduce((s, p) => s + p.length - 1, 0);
    return { g: 'filaments', pid: ri(rng, 1e9), N, di, par, pairs, rocks: best.rocks, req };
  },
  tap(p, r, c) {
    for (let i = 0; i < p.pairs.length; i++) {
      const pr = p.pairs[i];
      const isA = pr.a[0] === r && pr.a[1] === c,
        isB = pr.b[0] === r && pr.b[1] === c;
      if (isA || isB) {
        // COMPLÉTION : paire en main, tracé parti de l'AUTRE bout, extrémité adjacente à la tête
        if (sel === i && pr.path.length >= 1 && !pairDone(pr)) {
          const s = pr.path[0],
            h = pr.path[pr.path.length - 1];
          const fromOther =
            (isA && s[0] === pr.b[0] && s[1] === pr.b[1]) ||
            (isB && s[0] === pr.a[0] && s[1] === pr.a[1]);
          if (fromOther && Math.abs(h[0] - r) + Math.abs(h[1] - c) === 1) {
            pr.path.push([r, c]);
            return true;
          }
        }
        const hadPath = pr.path.length > 1;
        sel = i;
        pr.path = [[r, c]];
        return hadPath ? true : 'ui';
      }
    }
    if (sel == null) return false;
    const pr = p.pairs[sel];
    if (!pr.path.length) return false;
    for (let k = 0; k < pr.path.length; k++)
      if (pr.path[k][0] === r && pr.path[k][1] === c) {
        pr.path = pr.path.slice(0, k + 1);
        return true;
      }
    if (pairDone(pr)) return false;
    const [hr, hc] = pr.path[pr.path.length - 1];
    if (Math.abs(hr - r) + Math.abs(hc - c) !== 1) return false;
    for (const rk of p.rocks) if (rk[0] === r && rk[1] === c) return false;
    for (const o of p.pairs) {
      if (o === pr) continue;
      if ((o.a[0] === r && o.a[1] === c) || (o.b[0] === r && o.b[1] === c)) return false;
      for (const cc of o.path) if (cc[0] === r && cc[1] === c) return false;
    }
    pr.path.push([r, c]);
    return true;
  },
  resetSel() {
    sel = null;
  },
  status(p) {
    const N = p.N;
    if (!p.pairs.length) return { ok: false, txt: '⚠️ génération impossible — 🔀 Nouveau' };
    let done = 0,
      lenBad = 0;
    const cov = new Set<number>();
    for (const pr of p.pairs) {
      pr.path.forEach((c) => cov.add(c[0] * N + c[1]));
      if (pairDone(pr)) {
        done++;
        if (pr.goal != null) {
          if (p.di === 4) {
            if (pr.path.length < pr.goal) lenBad++;
          } else if (pr.path.length !== pr.goal) lenBad++;
        }
      }
    }
    const allDone = done === p.pairs.length;
    const covOk = !p.req.cover || cov.size === N * N;
    const lenOk = lenBad === 0;
    if (allDone && covOk && lenOk && done > 0)
      return { ok: true, txt: '🎉 <b>Tous les filaments coulent !</b>' };
    const bits = [`🔗 <b>${done}/${p.pairs.length}</b> paires`];
    if (p.req.cover) bits.push(`🌊 ${cov.size}/${N * N} cases`);
    if (p.req.lens || p.di === 4)
      bits.push(lenBad ? `📏 ${lenBad} longueur(s) fausse(s)` : '📏 ok');
    return { ok: false, txt: bits.join(' · ') };
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N,
      cs = S / N;
    ctx.fillStyle = '#081826';
    ctx.fillRect(0, 0, S, S);
    ctx.strokeStyle = 'rgba(255,255,255,.05)';
    ctx.lineWidth = 1;
    for (let i = 1; i < N; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cs, 0);
      ctx.lineTo(i * cs, S);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * cs);
      ctx.lineTo(S, i * cs);
      ctx.stroke();
    }
    for (const rk of p.rocks) {
      const x = rk[1] * cs,
        y = rk[0] * cs,
        pad = cs * 0.1;
      ctx.fillStyle = '#233042';
      ctx.strokeStyle = 'rgba(255,255,255,.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(x + pad, y + pad, cs - 2 * pad, cs - 2 * pad, cs * 0.22);
      ctx.fill();
      ctx.stroke();
    }
    const st = this.status(p);
    p.pairs.forEach((pr, i) => {
      const col = FL_COLS[pr.col];
      if (pr.path.length > 1) {
        ctx.strokeStyle = col;
        ctx.lineWidth = cs * 0.4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.globalAlpha = pairDone(pr) ? 1 : 0.75;
        if (st.ok) {
          ctx.shadowColor = col;
          ctx.shadowBlur = cs * 0.3;
        }
        ctx.beginPath();
        pr.path.forEach((c, k) => {
          const x = (c[1] + 0.5) * cs,
            y = (c[0] + 0.5) * cs;
          if (k) ctx.lineTo(x, y);
          else ctx.moveTo(x, y);
        });
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }
      for (const e of [pr.a, pr.b]) {
        const x = (e[1] + 0.5) * cs,
          y = (e[0] + 0.5) * cs;
        ctx.fillStyle = col;
        ctx.shadowColor = col;
        ctx.shadowBlur = cs * 0.25;
        ctx.beginPath();
        ctx.arc(x, y, cs * 0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        if (sel === i) {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(x, y, cs * 0.38, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      if (pr.goal != null) {
        const e = pr.a;
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${cs * 0.3}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((p.di === 4 ? '≥' : '') + pr.goal, (e[1] + 0.5) * cs, (e[0] + 0.5) * cs);
      }
    });
  },
  solveIt(p) {
    p.pairs.forEach((pr) => {
      pr.path = deep(pr.solPath);
    });
  },
};
